import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useProfilesData } from '@/features/user/api';
import { ENROLLMENT_API } from '@/features/user-notifications/utils/constants';
import { useNotifications } from '@/stores/notifications';

import {
  queryFunctionEnrollToCourse,
  queryFunctionEnrollToIMO,
  queryFunctionEnrollToNaplan,
} from './enroll-to-course';
import {
  getBatches,
  type GetBatchesParams,
  type GetBatchesResponse,
} from './get-batches';

// Query Keys
const enrollmentKeys = {
  all: ['enrollments'] as const,
  schoolCurriculum: () =>
    [
      ...enrollmentKeys.all,
      ENROLLMENT_API.queryKeys.enrollToSchoolCurriculum,
    ] as const,
  naplan: () =>
    [...enrollmentKeys.all, ENROLLMENT_API.queryKeys.enrollToNaplan] as const,
  imo: () =>
    [...enrollmentKeys.all, ENROLLMENT_API.queryKeys.enrollToIMO] as const,
} as const;

const getBatchEnrollmentKeys = {
  all: ['enrollments'] as const,
  batches: (params: GetBatchesParams) =>
    [...enrollmentKeys.all, 'batches', params] as const,
} as const;

/**
 * Internal mutation hook for school curriculum enrollment
 * Automatically enrolls for selected grade AND grade + 1
 */
const useEnrollToSchoolCurriculumMutation = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { selectedProfile } = useProfilesData();
  const email_id = selectedProfile?.email;

  return useMutation({
    mutationKey: enrollmentKeys.schoolCurriculum(),
    mutationFn: async (grade: string) =>
      queryFunctionEnrollToCourse({ grade, email_id: email_id ?? '' }),
    onSuccess: () => {
      // Invalidate specific queries that depend on enrollment data
      void queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['modules'] });

      // Show success notification
      addNotification({
        type: 'info',
        title: 'Enrolled to School Curriculum Successfully!',
      });
    },
    onError: (error: Error) => {
      // Show error notification
      addNotification({
        type: 'error',
        title: 'Failed to enroll to School Curriculum',
        message: error.message,
      });
    },
  });
};

/**
 * Public hook to enroll student to school curriculum course
 * Returns structured data instead of raw mutation object
 */
export const useEnrollToSchoolCurriculum = () => {
  const mutation = useEnrollToSchoolCurriculumMutation();

  return {
    enroll: mutation.mutate,
    enrollAsync: mutation.mutateAsync,
    isEnrolling: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
};

/**
 * Internal mutation hook for NAPLAN enrollment
 * Updated to support batch_id and profile_id
 */
const useEnrollToNaplanMutation = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { selectedProfile } = useProfilesData();
  const email_id = selectedProfile?.email;
  const profile_id = selectedProfile?.id;

  return useMutation({
    mutationKey: enrollmentKeys.naplan(),
    mutationFn: async (params: {
      grade: string;
      batch_id?: string;
      variant_id?: string;
    }) =>
      queryFunctionEnrollToNaplan({
        grade: params.grade,
        email_id: email_id ?? '',
        batch_id: params.batch_id,
        profile_id,
        variant_id: params.variant_id,
      }),
    onSuccess: () => {
      // Invalidate specific queries that depend on enrollment data
      void queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['modules'] });

      // Show success notification
      addNotification({
        type: 'info',
        title: 'Enrolled to NAPLAN Successfully!',
      });
    },
    onError: (error: Error) => {
      // Show error notification
      addNotification({
        type: 'error',
        title: 'Failed to enroll to NAPLAN',
        message: error.message,
      });
    },
  });
};

/**
 * Public hook to enroll student to NAPLAN course
 * Returns structured data instead of raw mutation object
 * Updated to accept batch_id and variant_id
 */
export const useEnrollToNaplan = () => {
  const mutation = useEnrollToNaplanMutation();

  return {
    enroll: (
      params: {
        grade: string;
        batch_id?: string;
        variant_id?: string;
      },
      options?: { onSuccess?: () => void }
    ) => {
      mutation.mutate(params, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
      });
    },
    enrollAsync: mutation.mutateAsync,
    isEnrolling: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
};

/**
 * Hook to fetch batches for NAPLAN enrollment
 * Based on the reference implementation from starry-night
 */
export const useBatches = (params: {
  grade?: string;
  product_id: string;
  variant_id?: string;
}) => {
  const { selectedProfile } = useProfilesData();
  const profile_id = selectedProfile?.id;

  const queryParams: GetBatchesParams = {
    grade: params.grade ?? '',
    product_id: params.product_id,
    profile_id,
    variant_id: params.variant_id,
  };

  return useQuery<GetBatchesResponse, Error>({
    queryKey: getBatchEnrollmentKeys.batches(queryParams),
    queryFn: () => getBatches(queryParams),
    enabled: !!(
      params.grade &&
      params.product_id &&
      profile_id &&
      params.grade.length > 0
    ),
  });
};

/**
 * Internal mutation hook for IMO enrollment
 */
const useEnrollToIMOMutation = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { selectedProfile } = useProfilesData();
  const email_id = selectedProfile?.email;
  const profile_id = selectedProfile?.id;

  return useMutation({
    mutationKey: enrollmentKeys.imo(),
    mutationFn: async (grade: string) =>
      queryFunctionEnrollToIMO({
        grade,
        email_id: email_id ?? '',
        profile_id,
      }),
    onSuccess: () => {
      // Invalidate specific queries that depend on enrollment data
      void queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['modules'] });

      // Show success notification
      addNotification({
        type: 'info',
        title: 'Enrolled to IMO Successfully!',
      });
    },
    onError: (error: Error) => {
      // Show error notification
      addNotification({
        type: 'error',
        title: 'Failed to enroll to IMO',
        message: error.message,
      });
    },
  });
};

/**
 * Public hook to enroll student to IMO course
 * Returns structured data instead of raw mutation object
 */
export const useEnrollToIMO = () => {
  const mutation = useEnrollToIMOMutation();

  return {
    enroll: (grade: string, options?: { onSuccess?: () => void }) => {
      mutation.mutate(grade, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
      });
    },
    enrollAsync: mutation.mutateAsync,
    isEnrolling: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
};

export { enrollmentKeys, getBatchEnrollmentKeys };
